import { useMemo } from 'react';
import { distinctUntilChanged, map, scan } from 'rxjs/operators';
import { useObservableValue } from '../../hooks/useObservable';
import { wsScanToDoubleMap, wsScanToMap } from '../../pipes';
import { SecurityTypesUtils } from '../../types';
import type { Security } from '../../types/Security';
import type { MinimalSubscriptionResponse } from '../../types/SubscriptionResponse';
import { EMPTY_ARRAY } from '../../utils/empty';
import { shouldDeleteSecurityFromIndex, type SecurityMemoryObservable } from './utils';

/** Fallback Map key when `isRelevant` is true but EventID is missing. Not used for display. */
export const UNLINKED_PREDICTION_EVENT_ID = '__UNLINKED_PREDICTION_EVENT_ID__';

export interface PredictionSecurityIndexes {
  predictionOutcomesBySymbol: Map<string, Security>;
  /** Keyed by PredictionOutcomeEventID. */
  predictionOutcomesByEventID: Map<string, Map<string, Security>>;
  predictionSupportedMarkets: string[];
}

export function getPredictionOutcomeEventID(security: Security): string | undefined {
  return security.PredictionOutcomeEventID;
}

/** PredictionOutcome securities always carry PredictionOutcomeEventID when indexed by event. */
export function isIndexedPredictionOutcome(security: Security): boolean {
  return SecurityTypesUtils.PredictionOutcome.isType(security) && getPredictionOutcomeEventID(security) != null;
}

/**
 * Supported markets are additive for the life of the stream. A Remove does not drop a venue,
 * because rebuilding from remaining constituents would mean scanning the full outcomes map on
 * every update. That is acceptable: prediction venues do not disappear in practice, and event
 * selectors still filter by the Markets currently advertised on outcomes.
 */
function scanSupportedMarkets(previous: string[], message: MinimalSubscriptionResponse<Security>): string[] {
  const markets = message.initial ? new Set<string>() : new Set(previous);
  for (const security of message.data ?? []) {
    if (!SecurityTypesUtils.PredictionOutcome.isType(security) || shouldDeleteSecurityFromIndex(security, message)) {
      continue;
    }
    for (const market of security.Markets ?? []) {
      markets.add(market);
    }
  }

  if (!message.initial && markets.size === previous.length) {
    return previous;
  }
  return markets.size === 0 ? EMPTY_ARRAY : [...markets];
}

export function usePredictionIndexes(securityMemoryObs: SecurityMemoryObservable) {
  const latestSecurityMessageObs = useMemo(
    () => securityMemoryObs.pipe(map(({ latestMessage }) => latestMessage)),
    [securityMemoryObs]
  );

  const predictionOutcomesBySymbolObs = useMemo(
    () =>
      latestSecurityMessageObs.pipe(
        wsScanToMap<Security, string>({
          getUniqueKey: security => security.Symbol,
          getInsertable: security => security,
          newMapEachUpdate: true,
          isRelevant: SecurityTypesUtils.PredictionOutcome.isType,
          shouldDelete: shouldDeleteSecurityFromIndex,
        })
      ),
    [latestSecurityMessageObs]
  );

  const predictionOutcomesByEventIDObs = useMemo(
    () =>
      latestSecurityMessageObs.pipe(
        wsScanToDoubleMap<Security, string, string>({
          getKey1: security => getPredictionOutcomeEventID(security) ?? UNLINKED_PREDICTION_EVENT_ID,
          getKey2: security => security.Symbol,
          getInsertable: security => security,
          newOuterMapEachUpdate: true,
          newInnerMapsEachUpdate: true,
          isRelevant: isIndexedPredictionOutcome,
          shouldDelete: shouldDeleteSecurityFromIndex,
          deleteInnerMapOnEmptied: true,
        })
      ),
    [latestSecurityMessageObs]
  );

  const predictionSupportedMarketsObs = useMemo(
    () => latestSecurityMessageObs.pipe(scan(scanSupportedMarkets, EMPTY_ARRAY), distinctUntilChanged()),
    [latestSecurityMessageObs]
  );

  const predictionOutcomesBySymbol = useObservableValue(
    () => predictionOutcomesBySymbolObs,
    [predictionOutcomesBySymbolObs],
    new Map()
  );
  const predictionOutcomesByEventID = useObservableValue(
    () => predictionOutcomesByEventIDObs,
    [predictionOutcomesByEventIDObs],
    new Map()
  );
  const predictionSupportedMarkets = useObservableValue(
    () => predictionSupportedMarketsObs,
    [predictionSupportedMarketsObs],
    EMPTY_ARRAY
  );

  return useMemo(
    () => ({
      predictionOutcomesBySymbol,
      predictionOutcomesByEventID,
      predictionSupportedMarkets,
    }),
    [predictionOutcomesByEventID, predictionOutcomesBySymbol, predictionSupportedMarkets]
  );
}
